import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { InspectorService } from '../../inspector/inspector.service';
import { Inspection, InspectionItem, InspectionVersion, Section } from '../../shared/interfaces';
import { VendorService } from '../../vendor/vendor.service';
import { VersionService } from '../version.service';

@Component({
  selector: 'app-version-clone-modal',
  templateUrl: './version-clone-modal.component.html',
  styleUrls: ['./version-clone-modal.component.scss'],
})
export class VersionCloneModalComponent implements OnInit {
  constructor(
    public vendorService: VendorService,
    public inspectorService: InspectorService,
    public versionService: VersionService,
    public router: Router,
    private toastr: ToastrService,
  ) {}

  items: InspectionItem[] = [];
  selectedItems: any[] = [];
  selectedSections: any[] = [];
  invalidQuestions = [];
  isValid: boolean = true;
  cloneEnabled: boolean = true;

  @ViewChild('cloneModal', { static: true }) public cloneModal: ModalDirective;
  @ViewChild('validationModal', { static: true }) public validationModal: ModalDirective;
  @Input() version: InspectionVersion;
  @Input() isCustomVersion: boolean = false;
  @Input() inspection: Inspection = null;
  @Output() clonedVersion = new EventEmitter<InspectionVersion>();

  async ngOnInit() {}

  async show() {
    await this.loadTree();
    this.selectAll();
    this.cloneModal.show();
  }

  cancel() {
    this.cloneModal.hide();
    this.validationModal.hide();
  }

  async loadTree() {
    const tree = await this.versionService.tree(this.version.id);
    this.items = tree.items.sort((a, b) => a.order - b.order) || [];
  }

  async clone() {
    const selectedSectionIds = this.selectedSections.map(section => section.id);
    let versionName = `${this.version.name} (CLONE)`;
    if (this.isCustomVersion) {
      const originalVersionName = this.version.name.split('|')[0].trim();
      versionName = `${originalVersionName} | ${this.inspection.policyNumber}`;
    }
    const version = await this.versionService.clone(
      this.version.id,
      versionName,
      selectedSectionIds,
      this.isCustomVersion,
    );
    if (!version.isValid) {
      this.invalidQuestions = await this.versionService.checkTree(version.id);
      this.validationModal.show();
    }
    this.toastr.success('Version cloned!');
    this.cloneModal.hide();
    this.clonedVersion.emit(version);
    if (!this.isCustomVersion) {
      this.router.navigateByUrl(`/version/form/${version.id}`);
    }
  }

  updateSelectedItems(item: InspectionItem) {
    const itemIndex = this.selectedItems.indexOf(item);
    if (itemIndex > -1) {
      this.selectedItems.splice(itemIndex, 1);
      this.deselectItemSections(item);
    } else {
      this.selectedItems.push(item);
      this.selectItemSections(item);
    }

    this.checkTree();
  }

  selectItemSections(item: InspectionItem) {
    item.sections.forEach(section => {
      if (!this.selectedSections.includes(section)) {
        this.selectedSections.push(section);
      }
    });
  }

  deselectItemSections(item: InspectionItem) {
    this.selectedSections = this.selectedSections.filter(section => !item.sections.includes(section));
  }

  updateSelectedSections(section: Section, item: InspectionItem) {
    const sectionIndex = this.selectedSections.indexOf(section);
    if (sectionIndex > -1) {
      this.selectedSections.splice(sectionIndex, 1);
    } else {
      this.selectedSections.push(section);
    }

    const areAnySectionsSelected = item.sections.some(sec => this.selectedSections.includes(sec));
    if (areAnySectionsSelected) {
      if (!this.selectedItems.includes(item)) {
        this.selectedItems.push(item);
      }
    } else {
      this.selectedItems = this.selectedItems.filter(i => i !== item);
    }

    this.checkTree();
  }

  toggleSelectAll($event: any) {
    $event.preventDefault();
    const selectAll = this.items.length === this.selectedItems.length;
    if (selectAll) {
      this.deselectAll();
    } else {
      this.selectAll();
    }
  }

  selectAll() {
    this.isValid = true;
    if (this.isCustomVersion) {
      this.cloneEnabled = false;
    }
    this.selectedItems = [...this.items];
    this.selectedSections = this.items.reduce((sections, item) => {
      return sections.concat(item.sections);
    }, []);
  }

  deselectAll() {
    this.selectedItems = [];
    this.selectedSections = [];
  }

  async checkTree() {
    if (this.isCustomVersion) {
      this.cloneEnabled = false;
    }

    const response = await this.versionService.checkInspectionVersion(
      this.version.id,
      this.selectedSections.map(section => section.id),
    );
    this.isValid = response.isValid;
    const allSelected =
      this.selectedSections.length === this.items.reduce((count, item) => count + item.sections.length, 0);
    setTimeout(() => {
      if (this.isCustomVersion) {
        this.cloneEnabled = !allSelected && this.isValid;
      }
    }, 0);
  }

  async fixValidationConditions() {
    this.validationModal.hide();
    // Assuming this.version.name is your dynamic string
    const encodedVersionName = encodeURIComponent(this.version.name);
    await this.router.navigateByUrl(`/question/tree?versionName=${encodedVersionName}`);
  }
}
